import { useEffect, useLayoutEffect, useState } from "react";
import { getAttributeProperties, getAttributesList } from "./api/attributes";
import { getElementProperties, getElementsList, getKindInfo, getPeriodInfo, getScopeInfo } from "./api/elements";
import { getRolesList } from "./api/roles";
import { getTeamsList } from "./api/teams";
import { LoadingErrorMessageConstants, LoadingMessageConstants } from "./constants/loading";
import { useAppDispatcher, useAppSelector } from "./hooks/redux";
import { IAPIResponse } from "./interfaces/api";
import { IAttributesInfo } from "./interfaces/attributes";
import { IElementKindInfo, IElementPeriodInfo, IElementAPIPropertiesResponse, IElementScopeInfo, IElementsInfo, ICoreElementProperties } from "./interfaces/elements";
import { IRoleInfo, IUserInfo } from "./interfaces/roles";
import { ITeamsInfo } from "./interfaces/teams";
import { ErrorPage } from "./screens/Error";
import { LandingPage3 } from "./screens/LandingPages/Page3";
import { LoadingPage } from "./screens/Loading";
import { addAttributesList, addAttributesPropertiesInfo, addElementPropertiesInfo, addElementsList, addKindInfo, addPeriodInfo, addScopeInfo, addTeamsList } from "./slices/properties";
import { addUserInfo, setLanId } from "./slices/user-info";
import { config, GetApplicationConfigurations } from "./utils/config";
import { log } from "./utils/logger";
import { checkAPIResponse, getQueryParameter, wait } from "./utils/utils";
import './assets/kendo/theme.scss';
import { StartSignalRConnection, StopSignalRConnection } from "./utils/signalr";

const packageInfo = require('../package.json');

const App = () => {
    const dispatcher = useAppDispatcher();
    const lanid: string = useAppSelector((state) => state.userInfo.lanid);
    const roleInfo: IRoleInfo = useAppSelector((state) => state.userInfo.roles);
    const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
    const [isInitialLoadComplete, setInitialLoadingComplete] = useState<boolean>(false);
    const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const getLanid = (): boolean => {
        const lanid: string | null = getQueryParameter('lanid');
        if (lanid != null && lanid !== "") {
            dispatcher(setLanId(lanid));
            return true;
        }
        else {
            return false;
        }
    }

    const setErrorState = (message: string): void => {
        setErrorOccurred(true);
        setErrorMessage(message);
        setInitialLoadingComplete(true);
    }

    const getInitialRequiredData = async (): Promise<void> => {
        setLoadingMessage(LoadingMessageConstants.loadingConfigurations);

        try {
            await GetApplicationConfigurations();
        } catch (error) {
            log.error(`Error while loading configurations`, error);
            setErrorMessage(LoadingErrorMessageConstants.loadingConfigurations)
            return;
        }

        await wait(1000);
        setLoadingMessage(LoadingMessageConstants.loadingUserInfo);

        try {
            const userInfoResponse: IAPIResponse<IUserInfo> = await getRolesList<IUserInfo>(lanid);
            const formattedResponse = checkAPIResponse<IUserInfo>(userInfoResponse);
            if (formattedResponse.status === true && userInfoResponse.response != null && userInfoResponse.response.data != null) {
                dispatcher(addUserInfo(userInfoResponse.response.data));
            }
            else {
                if (formattedResponse.message != null) {
                    setErrorState(`${LoadingErrorMessageConstants.loadingUserInfo} (${formattedResponse.message})`);
                }
                else {
                    setErrorState(`${LoadingErrorMessageConstants.loadingUserInfo}`);
                }
                return;
            }
        } catch (error) {
            log.error(`Error while loading user info`, error);
            setErrorState(LoadingErrorMessageConstants.loadingUserInfo);
            return;
        }
    }

    const getUserInfo = async (): Promise<void> => {
        await wait(1000);
        setLoadingMessage(LoadingMessageConstants.loadingTeams);

        try {
            const teamsInfoResponse: IAPIResponse<Array<ITeamsInfo>> = await getTeamsList<ITeamsInfo>(lanid);
            const formattedResponse = checkAPIResponse<Array<ITeamsInfo>>(teamsInfoResponse);
            if (formattedResponse.status === true && teamsInfoResponse.response != null && teamsInfoResponse.response.data != null) {
                dispatcher(addTeamsList(teamsInfoResponse.response.data));
            }
            else {
                if (formattedResponse.message != null) {
                    setErrorState(`${LoadingErrorMessageConstants.loadingTeams} (${formattedResponse.message})`);
                }
                else {
                    setErrorState(`${LoadingErrorMessageConstants.loadingTeams}`);
                }
                return;
            }
        } catch (error) {
            log.error(`Error while loading teams`, error);
            setErrorState(LoadingErrorMessageConstants.loadingTeams);
            return;
        }

        await wait(1000);
        setLoadingMessage(LoadingMessageConstants.loadingAttributes);

        try {
            const attributesInfoResponse: IAPIResponse<Array<IAttributesInfo>> = await getAttributesList<IAttributesInfo>(lanid);
            const formattedResponse = checkAPIResponse<Array<IAttributesInfo>>(attributesInfoResponse);
            if (formattedResponse.status === true && attributesInfoResponse.response != null && attributesInfoResponse.response.data != null) {
                dispatcher(addAttributesList(attributesInfoResponse.response.data));
            }
            else {
                if (formattedResponse.message != null) {
                    setErrorState(`${LoadingErrorMessageConstants.loadingAttributes} (${formattedResponse.message})`);
                }
                else {
                    setErrorState(`${LoadingErrorMessageConstants.loadingAttributes}`);
                }
                return;
            }
        } catch (error) {
            log.error(`Error while loading attributes`, error);
            setErrorState(LoadingErrorMessageConstants.loadingAttributes);
            return;
        }

        try {
            const attributesInfoResponse: IAPIResponse<ICoreElementProperties> = await getAttributeProperties();
            const formattedResponse = checkAPIResponse<ICoreElementProperties>(attributesInfoResponse);
            if (formattedResponse.status === true && attributesInfoResponse.response != null && attributesInfoResponse.response.data != null) {
                dispatcher(addAttributesPropertiesInfo(attributesInfoResponse.response.data));
            }
            else {
                if (formattedResponse.message != null) {
                    setErrorState(`${LoadingErrorMessageConstants.loadingAttributes} (${formattedResponse.message})`);
                }
                else {
                    setErrorState(`${LoadingErrorMessageConstants.loadingAttributes}`);
                }
                return;
            }
        } catch (error) {
            log.error(`Error while loading attributes`, error);
            setErrorState(LoadingErrorMessageConstants.loadingAttributes);
            return;
        }

        await wait(1000);
        setLoadingMessage(LoadingMessageConstants.loadingElements);

        try {
            const elementsInfoResponse: IAPIResponse<Array<IElementsInfo>> = await getElementsList<IElementsInfo>(lanid);
            const formattedResponse = checkAPIResponse<Array<IElementsInfo>>(elementsInfoResponse);
            if (formattedResponse.status === true && elementsInfoResponse.response != null && elementsInfoResponse.response.data != null) {
                dispatcher(addElementsList(elementsInfoResponse.response.data));
            }
            else {
                if (formattedResponse.message != null) {
                    setErrorState(`${LoadingErrorMessageConstants.loadingElements} (${formattedResponse.message})`);
                }
                else {
                    setErrorState(`${LoadingErrorMessageConstants.loadingElements}`);
                }
                return;
            }
        } catch (error) {
            log.error(`Error while loading elements`, error);
            setErrorState(LoadingErrorMessageConstants.loadingElements);
            return;
        }

        try {
            const elementsPropertiesInfoResponse: IAPIResponse<IElementAPIPropertiesResponse> = await getElementProperties();
            const formattedResponse = checkAPIResponse<IElementAPIPropertiesResponse>(elementsPropertiesInfoResponse);
            if (formattedResponse.status === true && elementsPropertiesInfoResponse.response != null && elementsPropertiesInfoResponse.response.data != null) {
                dispatcher(addElementPropertiesInfo(elementsPropertiesInfoResponse.response.data));
            }
            else {
                if (formattedResponse.message != null) {
                    setErrorState(`${LoadingErrorMessageConstants.loadingElements} (${formattedResponse.message})`);
                }
                else {
                    setErrorState(`${LoadingErrorMessageConstants.loadingElements}`);
                }
                return;
            }
        } catch (error) {
            log.error(`Error while loading elements`, error);
            setErrorState(LoadingErrorMessageConstants.loadingElements);
            return;
        }

        try {
            const periodInfoResponse: IAPIResponse<Array<IElementPeriodInfo>> = await getPeriodInfo();
            const formattedResponse = checkAPIResponse<Array<IElementPeriodInfo>>(periodInfoResponse);
            if (formattedResponse.status === true && periodInfoResponse.response != null && periodInfoResponse.response.data != null) {
                dispatcher(addPeriodInfo(periodInfoResponse.response.data));
            }
            else {
                if (formattedResponse.message != null) {
                    setErrorState(`${LoadingErrorMessageConstants.loadingElements} (${formattedResponse.message})`);
                }
                else {
                    setErrorState(`${LoadingErrorMessageConstants.loadingElements}`);
                }
                return;
            }
        } catch (error) {
            log.error(`Error while loading elements`, error);
            setErrorState(LoadingErrorMessageConstants.loadingElements);
            return;
        }

        try {
            const kindInfoResponse: IAPIResponse<Array<IElementKindInfo>> = await getKindInfo();
            const formattedResponse = checkAPIResponse<Array<IElementKindInfo>>(kindInfoResponse);
            if (formattedResponse.status === true && kindInfoResponse.response != null && kindInfoResponse.response.data != null) {
                dispatcher(addKindInfo(kindInfoResponse.response.data));
            }
            else {
                if (formattedResponse.message != null) {
                    setErrorState(`${LoadingErrorMessageConstants.loadingElements} (${formattedResponse.message})`);
                }
                else {
                    setErrorState(`${LoadingErrorMessageConstants.loadingElements}`);
                }
                return;
            }
        } catch (error) {
            log.error(`Error while loading elements`, error);
            setErrorState(LoadingErrorMessageConstants.loadingElements);
            return;
        }

        try {
            const scopeInfoResponse: IAPIResponse<Array<IElementScopeInfo>> = await getScopeInfo();
            const formattedResponse = checkAPIResponse<Array<IElementScopeInfo>>(scopeInfoResponse);
            if (formattedResponse.status === true && scopeInfoResponse.response != null && scopeInfoResponse.response.data != null) {
                dispatcher(addScopeInfo(scopeInfoResponse.response.data));
            }
            else {
                if (formattedResponse.message != null) {
                    setErrorState(`${LoadingErrorMessageConstants.loadingElements} (${formattedResponse.message})`);
                }
                else {
                    setErrorState(`${LoadingErrorMessageConstants.loadingElements}`);
                }
                return;
            }
        } catch (error) {
            log.error(`Error while loading elements`, error);
            setErrorState(LoadingErrorMessageConstants.loadingElements);
            return;
        }

        await wait(1000);
        setInitialLoadingComplete(true);

        await StartSignalRConnection(config.GamificationEngineBaseURL, lanid);
    }

    useLayoutEffect(() => {
        if (getLanid() === false) {
            setErrorMessage(LoadingErrorMessageConstants.loadingUserName);
            setErrorOccurred(true);
            setInitialLoadingComplete(true)
        }
    }, []);

    useEffect(() => {
        log.Info(`Gamification Admin Panel UI - ${packageInfo.version}`)

        return () => {
            log.Debug(`App component unmounted, stopping signalR connection`)
            StopSignalRConnection();
        }
    }, [])

    useEffect(() => {
        if (lanid !== '') {
            getInitialRequiredData();
        }
    }, [lanid]);

    useEffect(() => {
        if (errorOccurred === false && (roleInfo.maker === true || roleInfo.checker === true || roleInfo.previewer === true)) {
            getUserInfo();
        }
    }, [roleInfo]);

    return <>
        {isInitialLoadComplete === true ? (errorOccurred === true ? <ErrorPage message={errorMessage} /> : <LandingPage3 />) : <LoadingPage message={loadingMessage} />}
    </>

}

export default App;